import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Sparkles, MessageSquare, Zap, Check, AlertCircle, Bell, Tag, FileText, Send } from "lucide-react";
import slackIcon from "@/assets/slack.svg";
import gmailIcon from "@/assets/gmail.svg";

const TYPEWRITER_TEXT = "Draft a polite reply, add a VIP label, log to CRM, notify me on Slack, and send a follow-up.";
const TYPING_SPEED = 30;

const WORKFLOW_STEPS = [
  { icon: "sparkles", color: "purple", label: "AI Draft Reply", desc: "Contextual response generated" },
  { icon: "tag", color: "amber", label: "Add VIP Label", desc: "Contact tagged as high-priority" },
  { icon: "file", color: "sky", label: "Log to CRM", desc: "Lead entry created automatically" },
  { icon: "slack", color: "white", label: "Slack Notification", desc: "Team channel alerted" },
  { icon: "send", color: "emerald", label: "Send Follow-up", desc: "Scheduled for 24h later" },
];

const StepIcon = ({ type, color }: { type: string; color: string }) => {
  const colorMap: Record<string, string> = {
    purple: "text-purple-400", amber: "text-amber-400", sky: "text-sky-400",
    white: "text-white", emerald: "text-emerald-400",
  };
  const bgMap: Record<string, string> = {
    purple: "bg-purple-500/10", amber: "bg-amber-500/10", sky: "bg-sky-500/10",
    white: "bg-white/5", emerald: "bg-emerald-500/10",
  };
  const cls = `w-5 h-5 ${colorMap[color]}`;
  return (
    <div className={`w-9 h-9 rounded-lg ${bgMap[color]} flex items-center justify-center flex-shrink-0`}>
      {type === "sparkles" && <Sparkles className={cls} />}
      {type === "tag" && <Tag className={cls} />}
      {type === "file" && <FileText className={cls} />}
      {type === "slack" && <img src={slackIcon} alt="Slack" className="w-5 h-5" />}
      {type === "send" && <Send className={cls} />}
    </div>
  );
};

export const WorkflowAutomationSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [phase, setPhase] = useState(0); // 0=idle, 1=typing, 2=generating cards, 3=done badge, 4=notification
  const [typedText, setTypedText] = useState("");
  const [visibleCards, setVisibleCards] = useState(0);
  const [showDone, setShowDone] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  // Start on view
  useEffect(() => {
    if (!isInView) return;
    const timer = setTimeout(() => setPhase(1), 800);
    return () => clearTimeout(timer);
  }, [isInView]);

  // Typewriter effect
  useEffect(() => {
    if (phase !== 1) return;
    let i = 0;
    const interval = setInterval(() => {
      setTypedText(TYPEWRITER_TEXT.slice(0, i + 1));
      i++;
      if (i >= TYPEWRITER_TEXT.length) {
        clearInterval(interval);
        setTimeout(() => setPhase(2), 500);
      }
    }, TYPING_SPEED);
    return () => clearInterval(interval);
  }, [phase]);

  // Sequentially reveal 5 cards
  useEffect(() => {
    if (phase !== 2) return;
    setVisibleCards(0);
    let count = 0;
    const interval = setInterval(() => {
      count++;
      setVisibleCards(count);
      if (count >= WORKFLOW_STEPS.length) {
        clearInterval(interval);
        setTimeout(() => setPhase(3), 600);
      }
    }, 400);
    return () => clearInterval(interval);
  }, [phase]);

  // Show "Workflow Generated" badge then notification
  useEffect(() => {
    if (phase !== 3) return;
    setShowDone(true);
    const timer = setTimeout(() => setPhase(4), 1200);
    return () => clearTimeout(timer);
  }, [phase]);

  // Show notification animation then stop
  useEffect(() => {
    if (phase !== 4) return;
    setShowNotification(true);
  }, [phase]);

  return (
    <section id="features" className="py-24 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 bg-[#0A0F1C]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] bg-gradient-to-br from-blue-600/15 via-purple-600/10 to-transparent rounded-full blur-[120px]" />
      <div className="absolute inset-0 noise-texture opacity-20" />

      <div className="container relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="heading-lg mb-4">Workflow Automation</h2>
          <p className="body-lg max-w-2xl mx-auto text-muted-foreground">
            Build powerful automations with a simple, linear interface. No tangled node graphs.
            Just describe what you want, and ClearMakes builds the workflow.
          </p>
        </motion.div>

        {/* Live Workflow Animation */}
        <motion.div
          className="max-w-lg mx-auto mb-20"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 overflow-hidden min-h-[650px]">
            {/* Trigger Card */}
            <motion.div
              className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.05] border border-white/10"
              initial={{ opacity: 0, y: -10 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <img src={gmailIcon} alt="Gmail" className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-gray-300 font-medium">Trigger</span>
                <p className="text-sm font-medium text-white">New Lead in Gmail</p>
              </div>
            </motion.div>

            <div className="flex justify-center">
              <div className="w-px h-6 bg-gradient-to-b from-white/20 to-white/5" />
            </div>

            {/* Typewriter Input */}
            <AnimatePresence>
              {phase >= 1 && phase <= 2 && (
                <motion.div
                  className="p-3 rounded-xl bg-white/[0.04] border border-blue-500/20"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <span className="text-[10px] uppercase tracking-wider text-blue-400 font-medium block mb-1">
                    Your instruction
                  </span>
                  <p className="text-sm text-gray-300 font-mono">
                    {typedText}
                    <motion.span
                      className="inline-block w-0.5 h-4 bg-blue-400 ml-0.5 align-middle"
                      animate={{ opacity: phase === 1 ? [1, 0] : 0 }}
                      transition={{ duration: 0.5, repeat: phase === 1 ? Infinity : 0 }}
                    />
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* 5 Workflow Action Cards */}
            {phase >= 2 && WORKFLOW_STEPS.map((step, i) => (
              <div key={step.label}>
                <AnimatePresence>
                  {visibleCards > i && (
                    <>
                      <div className="flex justify-center">
                        <motion.div
                          className="w-px bg-gradient-to-b from-blue-500/30 to-blue-500/5"
                          initial={{ height: 0 }}
                          animate={{ height: 20 }}
                          transition={{ duration: 0.2 }}
                        />
                      </div>
                      <motion.div
                        className="flex items-center gap-3 p-3.5 rounded-xl bg-white/[0.05] border border-white/10"
                        initial={{ opacity: 0, y: -15, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ type: "spring", stiffness: 350, damping: 28 }}
                      >
                        <StepIcon type={step.icon} color={step.color} />
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] uppercase tracking-wider text-gray-300 font-medium">Step {i + 1}</span>
                          <p className="text-sm font-medium text-white">{step.label}</p>
                        </div>
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.15, type: "spring", stiffness: 500 }}
                        >
                          <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        </motion.div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            ))}

            {/* Workflow Generated Badge */}
            <AnimatePresence>
              {showDone && (
                <motion.div
                  className="mt-4 flex items-center justify-center gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  <motion.div
                    initial={{ rotate: -180, scale: 0 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  >
                    <Check className="w-5 h-5 text-emerald-400" />
                  </motion.div>
                  <span className="text-sm font-semibold text-emerald-300">Workflow Generated</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Notification Animation */}
            <AnimatePresence>
              {showNotification && (
                <motion.div
                  className="mt-3 space-y-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {/* Simulated Slack notification */}
                  <motion.div
                    className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/8"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.4 }}
                  >
                    <div className="w-7 h-7 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <img src={slackIcon} alt="Slack" className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] text-gray-300 font-medium">#sales-leads</p>
                      <p className="text-xs text-gray-300">New lead from Gmail: AI reply drafted and VIP label applied. Follow-up scheduled.</p>
                    </div>
                  </motion.div>

                  {/* Simulated email sent notification */}
                  <motion.div
                    className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/8"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.8 }}
                  >
                    <div className="w-7 h-7 rounded-md bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bell className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] text-gray-300 font-medium">ClearMakes</p>
                      <p className="text-xs text-gray-300">Reply sent to lead@company.com. CRM updated. Follow-up queued for tomorrow 9 AM.</p>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Comparison Visual */}
        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="grid md:grid-cols-2 gap-8">
            {/* The Old Way */}
            <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-8 relative overflow-hidden opacity-60">
              <div className="text-center mb-6">
                <span className="text-xs uppercase tracking-widest text-gray-300 font-medium">The Old Way</span>
              </div>
              <svg viewBox="0 0 280 180" className="w-full h-auto opacity-30" fill="none">
                <rect x="10" y="20" width="50" height="30" rx="6" fill="#374151" stroke="#4B5563" />
                <rect x="110" y="5" width="50" height="30" rx="6" fill="#374151" stroke="#4B5563" />
                <rect x="110" y="75" width="50" height="30" rx="6" fill="#374151" stroke="#4B5563" />
                <rect x="210" y="40" width="50" height="30" rx="6" fill="#374151" stroke="#4B5563" />
                <rect x="80" y="130" width="50" height="30" rx="6" fill="#374151" stroke="#4B5563" />
                <rect x="190" y="120" width="50" height="30" rx="6" fill="#374151" stroke="#4B5563" />
                <path d="M60 35 Q90 10 110 20" stroke="#4B5563" strokeWidth="1.5" fill="none" />
                <path d="M60 35 Q80 70 110 90" stroke="#4B5563" strokeWidth="1.5" fill="none" />
                <path d="M160 20 Q200 15 210 55" stroke="#4B5563" strokeWidth="1.5" fill="none" />
                <path d="M160 90 Q190 60 210 55" stroke="#4B5563" strokeWidth="1.5" fill="none" />
                <path d="M110 90 Q60 120 80 145" stroke="#4B5563" strokeWidth="1.5" fill="none" />
                <path d="M130 105 Q170 130 190 135" stroke="#4B5563" strokeWidth="1.5" fill="none" />
                <path d="M240 70 Q230 110 240 120" stroke="#4B5563" strokeWidth="1.5" fill="none" />
                <path d="M60 35 Q130 50 190 135" stroke="#4B5563" strokeWidth="1" strokeDasharray="4 4" fill="none" />
                <circle cx="135" cy="90" r="4" fill="#EF4444" />
              </svg>
              <div className="flex items-center justify-center gap-2 mt-4">
                <AlertCircle className="w-4 h-4 text-red-400/60" />
                <span className="text-sm text-gray-300 font-medium">Complex Node Graphs</span>
              </div>
            </div>

            {/* Our Way */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-transparent" />
              <div className="relative text-center mb-6">
                <span className="text-xs uppercase tracking-widest text-blue-400 font-medium">Our Way</span>
              </div>
              <div className="relative space-y-0 max-w-[220px] mx-auto">
                {WORKFLOW_STEPS.map((step, i) => (
                  <div key={step.label}>
                    {i > 0 && <div className="flex justify-center"><div className="w-px h-3 bg-blue-500/30" /></div>}
                    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-white/[0.06] border border-white/10">
                      {step.icon === "sparkles" && <Sparkles className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />}
                      {step.icon === "tag" && <Tag className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
                      {step.icon === "file" && <FileText className="w-3.5 h-3.5 text-sky-400 flex-shrink-0" />}
                      {step.icon === "slack" && <MessageSquare className="w-3.5 h-3.5 text-white flex-shrink-0" />}
                      {step.icon === "send" && <Send className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />}
                      <span className="text-[11px] text-white font-medium">{step.label}</span>
                      <Check className="w-3 h-3 text-emerald-400 ml-auto flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="relative flex items-center justify-center gap-2 mt-5">
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-white font-medium">Linear & Simple</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
